// Import react elements
import React, {
    useCallback,
    useEffect,
    useRef,
    useState
} from "react";

// Import global hooks
import usePageTitle from "global/hooks/usePageTitle";

// Import hooks
import useCustomProductsData from "../hooks/useCustomProductsData";

// Import components
import { Grid, GridItem } from "global/components/Grid";
import CustomProductsTable from "./components/CustomProductsTable";
import CustomProduct from "./components/CustomProduct";

// Import css stylesheet
import "./ManageCustomProducts.css";



// Custom products management page
const ManageCustomProductsPage = () => {

    // Set title inside the page
    usePageTitle("Manage Custom Products");

    // Store the custom product currently open in the editor popup
    const [selectedCustomProduct, setSelectedCustomProduct] = useState();

    // Set boolean state for adding a new custom product
    const [addingNewCustomProduct, setAddingNewCustomProduct] = useState(false);

    // Store the selected surface inside the open custom product
    const [selectedSurfaceData, setSelectedSurfaceData] = useState(null);

    // Store the selected print area inside the open custom product
    const [selectedPrintAreaData, setSelectedPrintAreaData] = useState(null);

    // Increment this whenever the editor closes so the list reflects saved changes
    const [customProductsRefreshKey, setCustomProductsRefreshKey] = useState(0);

    // Keep the custom products pagination page while viewing an individual product
    const [customProductsCurrentPage, setCustomProductsCurrentPage] = useState(1);

    // Keep the selected custom products page size while viewing an individual product
    const [customProductsRowsPerPage, setCustomProductsRowsPerPage] = useState(10);

    // Remember the browser position before opening a custom product
    const customProductsScrollPositionRef = useRef(0);

    // Track whether the list position should be restored when its data refreshes
    const restoreCustomProductsScrollPositionRef = useRef(false);


    // Custom hook for fetching and managing product data
    const {
        fetchAndFormatCustomProductMMMLinks,
        fetchMMMs
    } = useCustomProductsData();


    // Fetch and format material + method + mode links (with 'name' in the links)
    // State for storing MMMs
    // eslint-disable-next-line no-unused-vars
    const [materials, setMaterials] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [methods, setMethods] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [modes, setModes] = useState([]);

    // State for storing links
    const [links, setLinks] = useState([]);

    // Set loading state - while api call is fetching links
    const [, setLoadingLinks] = useState(true);


    // Function to refetch MMMs and then refetch+format links using those fresh MMMs
    const handleRefetchMMMsAndLinks = async (setMaterials, setMethods, setModes, setLinks, setLoadingLinks) => {

        // Await the MMM fetch and grab the fresh arrays directly
        const { materials, methods, modes } = await fetchMMMs(
            setMaterials,
            setMethods,
            setModes
        );

        // Pass the fresh MMM arrays into the formatter
        await fetchAndFormatCustomProductMMMLinks(
            setLinks,
            setLoadingLinks,
            materials,
            methods,
            modes
        );

    };


    // Load MMM data (materials, methods, modes) on mount
    useEffect(() => {

        // Call the async loader
        handleRefetchMMMsAndLinks(setMaterials, setMethods, setModes, setLinks, setLoadingLinks);

    // Dependency array is empty: only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // Remember the current custom products list position before opening a product
    const rememberCustomProductsScrollPosition = useCallback(() => {

        // Start with the browser window position
        let currentScrollPosition = window.scrollY;

        // Prefer the document scrolling element when available
        if (document.scrollingElement) {
            currentScrollPosition = document.scrollingElement.scrollTop;
        }

        // Store the current vertical position
        customProductsScrollPositionRef.current = currentScrollPosition;

        // Mark the position for restoration after the list refreshes
        restoreCustomProductsScrollPositionRef.current = true;

    }, []);


    // Clear the restoration flag after the list position has been restored
    const handleCustomProductsScrollPositionRestored = useCallback(() => {

        restoreCustomProductsScrollPositionRef.current = false;

    }, []);


    // Close the product editor and refresh the list underneath it
    const handleCustomProductEditorClose = () => {

        // Clear the selected nested records
        setSelectedSurfaceData(null);
        setSelectedPrintAreaData(null);

        // Clear the new-product state
        setAddingNewCustomProduct(false);

        // Close the editor popup
        setSelectedCustomProduct(null);

        // Refresh the list so saved names, deletes and new products are reflected immediately
        setCustomProductsRefreshKey((previousKey) => previousKey + 1);

    };


    return (

        <>

            {/* Keep the product list mounted underneath the editor popup */}
            <Grid>

                <GridItem>

                    <CustomProductsTable
                        setSelectedCustomProduct={setSelectedCustomProduct}
                        setAddingNewCustomProduct={setAddingNewCustomProduct}
                        currentPage={customProductsCurrentPage}
                        setCurrentPage={setCustomProductsCurrentPage}
                        rowsPerPage={customProductsRowsPerPage}
                        setRowsPerPage={setCustomProductsRowsPerPage}
                        customProductsScrollPosition={customProductsScrollPositionRef.current}
                        shouldRestoreCustomProductsScrollPosition={restoreCustomProductsScrollPositionRef.current}
                        rememberCustomProductsScrollPosition={rememberCustomProductsScrollPosition}
                        onCustomProductsScrollPositionRestored={handleCustomProductsScrollPositionRestored}
                        refreshKey={customProductsRefreshKey}
                    />

                </GridItem>

            </Grid>


            {/* Open the selected custom product in the standard large popup */}
            {selectedCustomProduct &&

                <CustomProduct
                    selectedCustomProduct={selectedCustomProduct}
                    setSelectedCustomProduct={setSelectedCustomProduct}
                    addingNewCustomProduct={addingNewCustomProduct}
                    selectedSurfaceData={selectedSurfaceData}
                    setSelectedSurfaceData={setSelectedSurfaceData}
                    selectedPrintAreaData={selectedPrintAreaData}
                    setSelectedPrintAreaData={setSelectedPrintAreaData}
                    customProductsMMMLinks={links}
                    onCloseEditor={handleCustomProductEditorClose}
                />

            }

        </>

    );

};

export default ManageCustomProductsPage;
